import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { STATUS_EMOJI, STATUS_MESSAGE_IDS } from '../../../services/certreq/userView/texts';
import { UserStatus } from '../../../services/certreq/userView/userStatus';

/*
 * Statusmerket på en kravrad — delt i to.
 *
 * Statusen var en gang ÉN ting: et merke med farge, ansikt og ordet «Mangler»
 * ved siden av radteksten. Merket er borte. Ordet gjentok seg nedover hele
 * siden uten å si søkeren noe fargen og ansiktet ikke allerede sa, og etiketten
 * stjal bredde fra selve kravet.
 *
 * Det som står igjen er to deler med hver sin mottaker:
 *
 *   - `UserStatusEmoji` — det **øyet** ser. Ren dekorasjon, `aria-hidden`, og
 *     plassert i radens venstre kolonne, der den står alene uten at noe legger
 *     seg under den.
 *   - `UserStatusText` — det **skjermleseren** hører. Statusordet, visuelt
 *     skjult, først på radens linje slik ordet stod før.
 *
 * At de er to komponenter og ikke én er hele poenget: emojien tegnes i en helt
 * annen del av rutenettet enn der ordet skal leses, og et krav inne i en gruppe
 * dropper emojien uten å miste statusen sin. Betydningen ligger derfor aldri i
 * farge alene.
 */

interface UserStatusProps {
  status: UserStatus;
}

/**
 * Statusordet, visuelt skjult.
 *
 * Det står **først i radens linje**, foran radteksten, slik den synlige
 * etiketten gjorde: en skjermleser skal få statusen før kravet, ikke etterpå.
 * Ordet er den eneste bæreren av status for den som ikke ser flatefargen, og
 * skal derfor stå på hver eneste rad — også på kravene inne i en gruppe, som
 * ikke har noen emoji.
 */
export const UserStatusText: React.FC<UserStatusProps> = ({ status }) => {
  const intl = useIntl();

  return <SrOnly>{intl.formatMessage({ id: STATUS_MESSAGE_IDS[status] })}</SrOnly>;
};

/**
 * Ansiktet som viser statusen.
 *
 * `notApplicable` har ingen emoji — en vei brukeren ikke følger skal være
 * stille — og da tegnes ingenting. Kalleren merker raden med `data-emoji`, slik
 * at venstrekolonnen faller bort når det ikke er noe å sette i den.
 */
export const UserStatusEmoji: React.FC<UserStatusProps> = ({ status }) =>
  STATUS_EMOJI[status] ? (
    <Emoji data-status-emoji="" aria-hidden="true">
      {STATUS_EMOJI[status]}
    </Emoji>
  ) : null;

/**
 * Oppskriften er den vanlige «visuelt skjult»: 1 px stor og klippet bort, men
 * fortsatt i tilgjengelighetstreet — til forskjell fra `display: none` og
 * `visibility: hidden`, som ville tatt teksten ut av lesningen også.
 *
 * Den er eksportert fordi den er sidens eneste oppskrift på «hørbar, men ikke
 * synlig» — skeletonen bruker den samme til beskjeden om at kravene hentes.
 */
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  border: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

/**
 * Ansiktet er skalert opp fra tekststørrelse, siden det ikke lenger har et ord
 * ved siden av seg til å bære betydningen. Det er radens eneste synlige
 * statusbærer, og skal kunne leses på en armlengdes avstand — derfor godt over
 * radtekstens 16 px.
 *
 * Boksen er like høy som radtekstens linje og sentrerer ansiktet i den, slik at
 * det står midt på første linje også når teksten under brytes over flere.
 *
 * De to margene er ren optisk justering, målt opp i nettleseren og ikke regnet
 * ut: emoji-glyffene tegnes høyt og med litt luft i forkant av sin egen
 * linjeboks, så en matematisk sentrert emoji leses som om den sitter for høyt og
 * for langt inn. Derfor de to piksel-verdiene — de er sanne for akkurat denne
 * størrelsen, og skal måles opp på nytt hvis `font-size` endres.
 */
const Emoji = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 1.35rem;
  font-size: 3rem;
  line-height: 1;
  margin-top: 5px;
  margin-left: -4px;
`;

export default UserStatusEmoji;
